import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import { type FC, useEffect, useRef } from 'react';
import { useStableCallback } from '../hooks/useStableCallback';
import { getTextAreaInputClassNames } from './TextAreaInput.styles';
import type { TextAreaInputProps } from './TextAreaInput.types';

export const TextAreaInput: FC<TextAreaInputProps> = ({
  value = '',
  size = 'default',
  dataTestId,
  ...props
}) => {
  const internalTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = () => {
    if (internalTextAreaRef.current) {
      internalTextAreaRef.current.style.height = 'auto';
      internalTextAreaRef.current.style.height = `${internalTextAreaRef.current.scrollHeight}px`;
    }
  };

  const resetHeight = useStableCallback(() => {
    if (internalTextAreaRef.current) {
      internalTextAreaRef.current.style.height = '';
    }
  });

  useEffect(() => {
    if (!props.autogrow) {
      resetHeight();
    }
  }, [props.autogrow]);

  // biome-ignore lint/correctness/useExhaustiveDependencies:  update height on size, autogrow change
  useEffect(() => {
    if (props.autogrow) {
      adjustHeight();
    }
  }, [value, props.autogrow, size]);

  return (
    <textarea
      className={getTextAreaInputClassNames(
        size,
        props.disabled,
        props.hasError,
        props.autogrow,
        props.className,
      )}
      autoFocus={props.autofocus}
      data-testid={dataTestId}
      disabled={props.disabled}
      value={value ?? ''}
      onChange={props.onChange}
      placeholder={props.placeholder}
      rows={props.autogrow ? 1 : 3}
      ref={mergeRefs(props.inputRef, internalTextAreaRef)}
      name={props.name}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      onKeyDown={props.onKeyDown}
      readOnly={props.readOnly}
    />
  );
};
